import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Archivo } from "@models/archivo.model";
import IEstadoDatatable from "@models/datatable.interface";
import { TipoDocumento } from "@models/tipo-documento.model";
import { assignToModel } from "@shared/utils/fieldsMap.utils";

@Injectable({ providedIn: "root" })
export class StateService {
	private tipoDocumento: TipoDocumento;
	private documento: Archivo;
	private estadoDatatableExpediente: IEstadoDatatable;
	private estadoDatatableLicencia: IEstadoDatatable;
	private conContraste: boolean;
    private renderer: Renderer2;
    private htmlElement: HTMLElement;
    private fontSizeLevel: 0 | 1 | 2;

    constructor(
         rendererFactory: RendererFactory2,
        @Inject(DOCUMENT) private document: Document
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
        this.htmlElement = this.document.documentElement; 
    }

	setTipoDocumento(td: TipoDocumento) {
		this.tipoDocumento = td;
		this.actualizarStateTipoDocumento();
	}

	getTipoDocumento(): TipoDocumento {
		if (!this.tipoDocumento && history.state?.tipoDocumento) {
			this.setTipoDocumento(assignToModel(TipoDocumento, history.state?.tipoDocumento));
		}
		if (this.tipoDocumento && !history.state?.tipoDocumento)
			this.actualizarStateTipoDocumento();
		return this.tipoDocumento;
	}

	setDocumento(d: Archivo) {
		this.documento = d;
		this.actualizarStateDocumento();
	}

	getDocumento(): Archivo {
		if (!this.documento && history.state?.documento) {
			this.setDocumento(assignToModel(Archivo, history.state?.documento));
		}
		if (this.documento && !history.state?.documento)
			this.actualizarStateDocumento();
		return this.documento;
	}

	setEstadoDatatableExpediente(e: IEstadoDatatable) {
		this.estadoDatatableExpediente = e;
		this.actualizarStateDatatableExpediente();
	}

	getEstadoDatatableExpediente(): IEstadoDatatable {
		if (
			!this.estadoDatatableExpediente &&
			history.state?.estadoDatatableExpediente
		) {
			this.setEstadoDatatableExpediente(
				history.state?.estadoDatatableExpediente
			);
		}
		if (
			this.estadoDatatableExpediente &&
			!history.state?.estadoDatatableExpediente
		)
			this.actualizarStateDatatableExpediente();
		return this.estadoDatatableExpediente;
	}

	setEstadoDatatableLicencia(e: IEstadoDatatable) {
		this.estadoDatatableLicencia = e;
		this.actualizarStateDatatableLicencia();
	}

	getEstadoDatatableLicencia(): IEstadoDatatable {
		if (
			!this.estadoDatatableLicencia &&
			history.state?.estadoDatatableLicencia
		) {
			this.setEstadoDatatableLicencia(
				history.state?.estadoDatatableLicencia
			);
		}
		if (
			this.estadoDatatableLicencia &&
			!history.state?.estadoDatatableLicencia
		)
			this.actualizarStateDatatableLicencia();
		return this.estadoDatatableLicencia;
	}

	setContraste(conContraste: boolean) {
		this.conContraste = conContraste;
   
        if (this.conContraste) this.renderer.addClass(this.htmlElement, "a11y-contrast");
        else this.renderer.removeClass(this.htmlElement, 'a11y-contrast');

		this.actualizarStateContraste();
	}

	getContraste(): void {
		if (typeof this.conContraste !== "boolean" && typeof history.state?.conContraste === "boolean") {
			this.setContraste(history.state?.conContraste);
		}
		if (typeof this.conContraste === "boolean" && typeof history.state?.conContraste !== "boolean")
			this.actualizarStateContraste();
  
        if (this.conContraste) this.renderer.addClass(this.htmlElement, "a11y-contrast");
	}

	setFontSize(size: 0 | 1 | 2): void {
		this.fontSizeLevel = size;
        this.updateFontSizeClass();
		this.actualizarStateFontSize();
	}

	getFontSize(): 0 | 1 | 2 {
		if (![0, 1, 2].includes(this.fontSizeLevel) && [0, 1, 2].includes(history.state?.fontSizeLevel)) {
			this.setFontSize(history.state?.fontSizeLevel);
		}
		if ([0, 1, 2].includes(this.fontSizeLevel) && ![0, 1, 2].includes(history.state?.fontSizeLevel))
			this.actualizarStateFontSize();
  
        this.updateFontSizeClass();

        return this.fontSizeLevel??0;
	}

	private actualizarStateTipoDocumento() {
		history.replaceState({ ...history.state, tipoDocumento: this.tipoDocumento }, "");
	}

	private actualizarStateDocumento() {
		history.replaceState({ ...history.state, documento: this.documento }, "");
	}

	private actualizarStateDatatableExpediente() {
		history.replaceState(
			{
				...history.state,
				estadoDatatableExpediente: this.estadoDatatableExpediente,
			},
			""
		);
	}

	private actualizarStateDatatableLicencia() {
		history.replaceState(
			{
				...history.state,
				estadoDatatableLicencia: this.estadoDatatableLicencia,
			},
			""
		);
	}

	private actualizarStateContraste() {
		history.replaceState(
			{ ...history.state, conContraste: this.conContraste },
			""
		);
	}

	private actualizarStateFontSize() {
		history.replaceState(
			{ ...history.state, fontSizeLevel: this.fontSizeLevel },
			""
		);
	}

    private updateFontSizeClass(): void {
        this.renderer.removeClass(this.htmlElement, "a11y-font-0");
        this.renderer.removeClass(this.htmlElement, "a11y-font-1");
        this.renderer.removeClass(this.htmlElement, "a11y-font-2");

        switch (this.fontSizeLevel) {
            case 0:
                this.renderer.addClass(this.htmlElement, "a11y-font-0");
                break;
            case 1:
                this.renderer.addClass(this.htmlElement, "a11y-font-1");
                break;
            case 2:
                this.renderer.addClass(this.htmlElement, "a11y-font-2");
                break;
        }
    }
}
